import { Head, router } from "@inertiajs/react"
import { useCallback, useState } from "react"

import { Button } from "@/components/ui/button"
import PartnerSettingsLayout from "@/layouts/partner/settings-layout"
import PartnerLayout from "@/layouts/partner-layout"
import { partnerSettingsSecurityPath } from "@/routes"

interface Provider {
  uid: string
  kind: string
  name: string
  protocol: string | null
  state: string
  connected_accounts_count: number
}

interface Policies {
  enforce_sso: boolean
  auto_provision_sso: boolean
}

const PROVIDER_LOGOS: Record<string, string> = {
  entra:
    "https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg",
  google:
    "https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_128dp.png",
  saml: "",
}

function Toggle({
  enabled,
  onToggle,
}: {
  enabled: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
        enabled ? "bg-zinc-950 dark:bg-white" : "bg-zinc-200 dark:bg-zinc-700"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform dark:bg-zinc-950 ${
          enabled ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  )
}

function SamlIcon() {
  return (
    <div className="flex size-full items-center justify-center text-xs font-bold text-zinc-500">
      SSO
    </div>
  )
}

export default function SecuritySettings({
  providers,
  policies,
}: {
  providers: Provider[]
  policies: Policies
}) {
  const [enforceSSO, setEnforceSSO] = useState(policies.enforce_sso)
  const [autoProvision, setAutoProvision] = useState(
    policies.auto_provision_sso,
  )

  const updatePolicy = useCallback((key: string, value: boolean) => {
    router.patch(
      partnerSettingsSecurityPath(),
      { policies: { [key]: value } },
      { preserveState: true, preserveScroll: true },
    )
  }, [])

  return (
    <PartnerLayout>
      <Head title="Settings — Security" />
      <PartnerSettingsLayout>
        <div className="flex flex-col gap-8">
          {/* Identity providers */}
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-sm font-semibold">Identity providers</h2>
              <p className="text-muted-foreground mt-1 text-sm">
                Connect identity providers to enable SSO for your accounts.
              </p>
            </div>
            <div>
              {providers.map((provider) => {
                const logoUrl = PROVIDER_LOGOS[provider.kind]
                const isConnected = provider.state === "connected"
                return (
                  <div
                    key={provider.uid}
                    className="flex items-center gap-4 border-b py-4 last:border-0"
                  >
                    <div className="bg-muted flex size-12 shrink-0 items-center justify-center rounded-lg p-2.5">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={provider.name}
                          className="size-full object-contain"
                        />
                      ) : (
                        <SamlIcon />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {provider.name}
                        </span>
                        {isConnected && (
                          <span className="size-2 shrink-0 rounded-full bg-green-500" />
                        )}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        {provider.protocol}
                        {isConnected &&
                          ` · ${provider.connected_accounts_count} accounts`}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={isConnected ? "outline" : "default"}
                      className="shrink-0 rounded-sm"
                    >
                      {isConnected ? "Configure" : "Connect"}
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Policies */}
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-sm font-semibold">Policies</h2>
              <p className="text-muted-foreground mt-1 text-sm">
                Control how SSO is applied across your accounts.
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between border-b py-4">
                <div>
                  <div className="text-sm font-medium">
                    Enforce SSO for all accounts
                  </div>
                  <div className="text-muted-foreground text-sm">
                    Disable email/password login for all managed accounts
                  </div>
                </div>
                <Toggle
                  enabled={enforceSSO}
                  onToggle={() => {
                    const next = !enforceSSO
                    setEnforceSSO(next)
                    updatePolicy("enforce_sso", next)
                  }}
                />
              </div>
              <div className="flex items-center justify-between py-4">
                <div>
                  <div className="text-sm font-medium">
                    Auto-provision on first login
                  </div>
                  <div className="text-muted-foreground text-sm">
                    Automatically create accounts for users signing in via SSO
                  </div>
                </div>
                <Toggle
                  enabled={autoProvision}
                  onToggle={() => {
                    const next = !autoProvision
                    setAutoProvision(next)
                    updatePolicy("auto_provision_sso", next)
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </PartnerSettingsLayout>
    </PartnerLayout>
  )
}
